import { useSEO } from '../../../utils/seo';
import Icon from '../../../components/base/Icon';

export default function KitchenRemodelCostBoiseIdaho() {
  useSEO({
    title: 'Kitchen Remodel Cost in Boise, Idaho (2025)',
    description: 'Complete kitchen remodel cost guide for Boise homeowners. Budget, mid-range & high-end pricing. ROI data for Treasure Valley. Get accurate estimates.',
    keywords: 'kitchen remodel cost Boise Idaho, kitchen renovation Meridian, kitchen remodel ROI Treasure Valley, kitchen contractor Boise, kitchen renovation cost Idaho',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/blog/kitchen-remodel-cost-boise-idaho`,
    ogType: 'article',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: 'Kitchen Remodel Cost in Boise, Idaho (2025)',
          datePublished: '2025-01-15',
          dateModified: '2025-01-15',
          author: {
            '@type': 'Organization',
            name: 'Fix Right Services',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Fix Right Services',
            logo: {
              '@type': 'ImageObject',
              url: 'https://static.readdy.ai/fixrightservices/logo.svg',
            },
          },
          image: 'https://public.readdy.ai/ai/img_res/84f74964a8718b4c89b97f9b9d547c26.jpg',
          keywords: 'kitchen remodel cost Boise Idaho, kitchen renovation Meridian, kitchen remodel ROI Treasure Valley',
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How much does a kitchen remodel cost in Boise, Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The average kitchen remodel in Boise costs $25,000-$75,000. Budget remodels range from $15,000-$30,000, mid-range remodels $30,000-$60,000, and high-end remodels $60,000-$100,000+. Costs vary based on kitchen size, materials, and scope of work.',
              },
            },
            {
              '@type': 'Question',
              name: 'What is the ROI on a kitchen remodel in Treasure Valley?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Kitchen remodels in the Treasure Valley typically return 60-80% of investment at resale. Minor kitchen remodels (under $30,000) often return 70-80%, while major upscale remodels return 50-60%. Kitchens are one of the highest ROI home improvements.',
              },
            },
            {
              '@type': 'Question',
              name: 'How long does a kitchen remodel take in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Most kitchen remodels in Boise take 6-12 weeks from start to finish. Minor cosmetic updates take 2-4 weeks, while full gut renovations with layout changes can take 12-16 weeks. Timeline depends on material availability and permit processing.',
              },
            },
            {
              '@type': 'Question',
              name: 'What should be included in a kitchen remodel budget?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'A complete kitchen remodel budget should include: cabinets (30-40%), countertops (10-15%), appliances (15-20%), labor (20-35%), flooring (7-10%), lighting and fixtures (5-8%), and a 10-20% contingency for unexpected costs.',
              },
            },
            {
              '@type': 'Question',
              name: 'Do I need permits for a kitchen remodel in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, most kitchen remodels in Boise require permits for electrical, plumbing, and structural work. Cosmetic updates like painting and new cabinet doors typically don\'t require permits. Your contractor should handle all permit applications.',
              },
            },
            {
              '@type': 'Question',
              name: 'How do I choose a kitchen remodeling contractor in Boise?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Choose a licensed, insured contractor with kitchen remodeling experience in Boise. Check Idaho contractor license, read reviews, view past projects, get 3+ detailed quotes, verify insurance, and ensure they handle permits. Ask for references from recent Treasure Valley projects.',
              },
            },
          ],
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://fixrightservices.org/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: 'https://fixrightservices.org/blog',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'Kitchen Remodel Cost in Boise, Idaho (2025)',
              item: 'https://fixrightservices.org/blog/kitchen-remodel-cost-boise-idaho',
            },
          ],
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center gap-2 cursor-pointer">
              <img src="/images/logo.svg" alt="Fix Right Services" className="h-10 w-auto" />
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/#services" className="text-gray-700 hover:text-[#B45309] font-medium cursor-pointer whitespace-nowrap">
                Services
              </a>
              <a href="/blog" className="text-[#B45309] font-medium cursor-pointer whitespace-nowrap">
                Blog
              </a>
              <a href="/#contact" className="bg-[#B45309] text-white px-6 py-2.5 rounded-lg hover:bg-[#92400E] font-semibold cursor-pointer whitespace-nowrap">
                Get Free Quote
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#B45309]/5 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm mb-6">
            <a href="/" className="text-gray-500 hover:text-[#B45309] cursor-pointer">Home</a>
            <Icon name="arrow-right" className="w-3 h-3 text-gray-400" />
            <a href="/blog" className="text-gray-500 hover:text-[#B45309] cursor-pointer">Blog</a>
            <Icon name="arrow-right" className="w-3 h-3 text-gray-400" />
            <span className="text-gray-900">Kitchen Remodel Cost</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Kitchen Remodel Cost in Boise, Idaho (2025)
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
            <span className="flex items-center gap-2">
              <Icon name="calendar" className="w-4 h-4" />
              January 15, 2025
            </span>
            <span className="flex items-center gap-2">
              <Icon name="time" className="w-4 h-4" />
              10 min read
            </span>
          </div>
          <img
            src="https://readdy.ai/api/search-image?query=modern%20kitchen%20remodel%20boise%20idaho%20white%20cabinets%20quartz%20countertops%20stainless%20appliances%20bright%20natural%20light%20clean%20contemporary%20design%20professional%20interior%20photography%20high%20quality&width=1200&height=630&seq=kitchen-remodel-hero&orientation=landscape"
            alt="Kitchen Remodel in Boise Idaho"
            className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
            loading="eager"
          />
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            A kitchen remodel is one of the most valuable home improvements you can make in Boise, Idaho. Whether you're updating an outdated kitchen or creating your dream cooking space, understanding the costs helps you plan effectively and maximize your investment.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Average Kitchen Remodel Cost in Boise</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            The average kitchen remodel in Boise costs between <strong>$25,000 and $75,000</strong>. However, costs vary significantly based on the scope of work, materials selected, and kitchen size.
          </p>
          <div className="bg-[#B45309]/5 border-l-4 border-[#B45309] p-6 rounded-r-lg mb-8">
            <p className="text-gray-800 font-semibold mb-2">2025 Kitchen Remodel Cost Ranges:</p>
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Budget Remodel:</strong> $15,000 - $30,000</li>
              <li>• <strong>Mid-Range Remodel:</strong> $30,000 - $60,000</li>
              <li>• <strong>High-End Remodel:</strong> $60,000 - $100,000+</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Kitchen Remodel Cost by Tier</h2>
          
          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Budget Kitchen Remodel ($15,000 - $30,000)</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            A budget kitchen remodel focuses on <strong>cosmetic updates and strategic improvements</strong> without changing the layout.
          </p>
          <p className="text-gray-700 font-semibold mb-2">What's Included:</p>
          <ul className="space-y-2 text-gray-700 mb-6 ml-6">
            <li>• Cabinet refacing or painting (not replacement)</li>
            <li>• Laminate or basic quartz countertops</li>
            <li>• Mid-grade appliances (one or two replacements)</li>
            <li>• Vinyl or laminate flooring</li>
            <li>• New hardware, faucet, and lighting</li>
            <li>• Fresh paint</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Best for:</strong> First-time homebuyers, rental properties, or homeowners planning to sell within 3-5 years.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Mid-Range Kitchen Remodel ($30,000 - $60,000)</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            The most popular choice for Boise homeowners, offering <strong>significant upgrades with excellent ROI</strong>.
          </p>
          <p className="text-gray-700 font-semibold mb-2">What's Included:</p>
          <ul className="space-y-2 text-gray-700 mb-6 ml-6">
            <li>• Semi-custom cabinets (full replacement)</li>
            <li>• Quartz or granite countertops</li>
            <li>• Stainless steel appliance package</li>
            <li>• Tile or engineered hardwood flooring</li>
            <li>• Tile backsplash</li>
            <li>• Undermount sink and quality faucet</li>
            <li>• Recessed lighting and pendant fixtures</li>
            <li>• Minor layout changes (moving sink or adding island)</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Best for:</strong> Long-term homeowners in Meridian, Eagle, and established Boise neighborhoods looking to modernize and increase home value.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">High-End Kitchen Remodel ($60,000 - $100,000+)</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            A luxury kitchen remodel with <strong>premium materials, custom features, and major layout changes</strong>.
          </p>
          <p className="text-gray-700 font-semibold mb-2">What's Included:</p>
          <ul className="space-y-2 text-gray-700 mb-6 ml-6">
            <li>• Custom cabinets with specialty finishes</li>
            <li>• Premium quartz, marble, or quartzite countertops</li>
            <li>• High-end appliance package (Sub-Zero, Wolf, etc.)</li>
            <li>• Hardwood or luxury tile flooring</li>
            <li>• Designer backsplash and custom tile work</li>
            <li>• Structural changes (removing walls, adding windows)</li>
            <li>• Smart home integration</li>
            <li>• Custom range hood and built-ins</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Best for:</strong> High-end homes in North Boise, Eagle, and luxury Treasure Valley properties where homeowners plan to stay 10+ years.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Kitchen Remodel Cost Breakdown</h2>
          <p className="text-gray-700 leading-relaxed mb-4">Understanding where your money goes helps you prioritize spending:</p>
          <div className="space-y-3 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-32 font-semibold text-gray-900">Cabinets</div>
              <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                <div className="bg-[#B45309] h-6 rounded-full" style={{width: '35%'}}></div>
              </div>
              <div className="w-16 text-right font-semibold text-gray-900">30-40%</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32 font-semibold text-gray-900">Labor</div>
              <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                <div className="bg-[#B45309] h-6 rounded-full" style={{width: '25%'}}></div>
              </div>
              <div className="w-16 text-right font-semibold text-gray-900">20-35%</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32 font-semibold text-gray-900">Appliances</div>
              <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                <div className="bg-[#B45309] h-6 rounded-full" style={{width: '17%'}}></div>
              </div>
              <div className="w-16 text-right font-semibold text-gray-900">15-20%</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32 font-semibold text-gray-900">Countertops</div>
              <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                <div className="bg-[#B45309] h-6 rounded-full" style={{width: '12%'}}></div>
              </div>
              <div className="w-16 text-right font-semibold text-gray-900">10-15%</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32 font-semibold text-gray-900">Flooring</div>
              <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                <div className="bg-[#B45309] h-6 rounded-full" style={{width: '8%'}}></div>
              </div>
              <div className="w-16 text-right font-semibold text-gray-900">7-10%</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32 font-semibold text-gray-900">Lighting</div>
              <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                <div className="bg-[#B45309] h-6 rounded-full" style={{width: '6%'}}></div>
              </div>
              <div className="w-16 text-right font-semibold text-gray-900">5-8%</div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Kitchen Remodel ROI in Treasure Valley</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Kitchen remodels consistently deliver strong returns in the Boise real estate market. According to recent data, <strong>kitchen renovations return 60-80% of investment</strong> at resale.
          </p>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3">Minor Kitchen Remodel</h4>
              <p className="text-sm text-gray-700 mb-2"><strong>Cost:</strong> $15,000 - $30,000</p>
              <p className="text-sm text-gray-700 mb-2"><strong>ROI:</strong> 70-80%</p>
              <p className="text-sm text-gray-700">Best for homes under $400,000 in Boise, Nampa, and Caldwell.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-3">Major Kitchen Remodel</h4>
              <p className="text-sm text-gray-700 mb-2"><strong>Cost:</strong> $60,000 - $100,000+</p>
              <p className="text-sm text-gray-700 mb-2"><strong>ROI:</strong> 50-60%</p>
              <p className="text-sm text-gray-700">Best for homes over $500,000 in Eagle, North Boise, and luxury markets.</p>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Key insight:</strong> Mid-range remodels ($30,000-$60,000) typically offer the best balance of cost and ROI in the Treasure Valley market.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">How to Choose a Kitchen Remodeling Contractor in Boise</h2>
          <div className="space-y-4 mb-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-[#B45309] text-white rounded-full flex items-center justify-center font-bold">1</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Verify License & Insurance</h4>
                <p className="text-gray-700">Confirm Idaho contractor license, general liability insurance, and workers' compensation coverage. Check license status at <strong>ibol.idaho.gov</strong>.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-[#B45309] text-white rounded-full flex items-center justify-center font-bold">2</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Review Past Kitchen Projects</h4>
                <p className="text-gray-700">Ask to see completed kitchen remodels in the Treasure Valley. Look for quality craftsmanship, attention to detail, and design consistency.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-[#B45309] text-white rounded-full flex items-center justify-center font-bold">3</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Get Detailed Written Quotes</h4>
                <p className="text-gray-700">Obtain at least 3 itemized quotes. Beware of quotes significantly lower than others—they often indicate cut corners or hidden costs.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-[#B45309] text-white rounded-full flex items-center justify-center font-bold">4</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Check References & Reviews</h4>
                <p className="text-gray-700">Read Google reviews, check BBB rating, and call recent references. Ask about communication, timeline adherence, and problem-solving.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-[#B45309] text-white rounded-full flex items-center justify-center font-bold">5</div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Confirm Permit Handling</h4>
                <p className="text-gray-700">Your contractor should handle all permit applications and inspections. This ensures code compliance and protects your investment.</p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6 mb-12">
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">How much does a kitchen remodel cost in Boise, Idaho?</h4>
              <p className="text-gray-700">The average kitchen remodel in Boise costs $25,000-$75,000. Budget remodels range from $15,000-$30,000, mid-range remodels $30,000-$60,000, and high-end remodels $60,000-$100,000+. Costs vary based on kitchen size, materials, and scope of work.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">What is the ROI on a kitchen remodel in Treasure Valley?</h4>
              <p className="text-gray-700">Kitchen remodels in the Treasure Valley typically return 60-80% of investment at resale. Minor kitchen remodels (under $30,000) often return 70-80%, while major upscale remodels return 50-60%. Kitchens are one of the highest ROI home improvements.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">How long does a kitchen remodel take in Boise?</h4>
              <p className="text-gray-700">Most kitchen remodels in Boise take 6-12 weeks from start to finish. Minor cosmetic updates take 2-4 weeks, while full gut renovations with layout changes can take 12-16 weeks. Timeline depends on material availability and permit processing.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">What should be included in a kitchen remodel budget?</h4>
              <p className="text-gray-700">A complete kitchen remodel budget should include: cabinets (30-40%), countertops (10-15%), appliances (15-20%), labor (20-35%), flooring (7-10%), lighting and fixtures (5-8%), and a 10-20% contingency for unexpected costs.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">Do I need permits for a kitchen remodel in Boise?</h4>
              <p className="text-gray-700">Yes, most kitchen remodels in Boise require permits for electrical, plumbing, and structural work. Cosmetic updates like painting and new cabinet doors typically don\'t require permits. Your contractor should handle all permit applications.</p>
            </div>
            <div className="border-l-4 border-[#B45309] pl-6">
              <h4 className="font-bold text-gray-900 mb-2">How do I choose a kitchen remodeling contractor in Boise?</h4>
              <p className="text-gray-700">Choose a licensed, insured contractor with kitchen remodeling experience in Boise. Check Idaho contractor license, read reviews, view past projects, get 3+ detailed quotes, verify insurance, and ensure they handle permits. Ask for references from recent Treasure Valley projects.</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#B45309] to-[#92400E] text-white p-8 rounded-xl mb-12">
            <h3 className="text-2xl font-bold mb-3">Ready to Remodel Your Kitchen?</h3>
            <p className="mb-6 text-white/90">Get a free, detailed quote from Fix Right Services. We specialize in kitchen remodels throughout Boise, Meridian, Eagle, and the Treasure Valley.</p>
            <a href="/#contact" className="inline-flex items-center gap-2 bg-white text-[#B45309] px-8 py-3 rounded-lg font-bold hover:bg-gray-100 cursor-pointer whitespace-nowrap">
              Get Free Quote
              <Icon name="arrow-right" className="w-4 h-4" />
            </a>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Related Articles</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <a href="/blog/best-roi-home-remodels-boise" className="block bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
              <h4 className="font-bold text-gray-900 mb-2">Best ROI Home Remodels in Boise for 2025</h4>
              <p className="text-sm text-gray-600 mb-3">Discover which home improvement projects deliver the highest returns in the Treasure Valley market.</p>
              <span className="text-[#B45309] font-semibold text-sm flex items-center gap-2">
                Read More
                <Icon name="arrow-right" className="w-3.5 h-3.5" />
              </span>
            </a>
            <a href="/blog/bathroom-renovation-roi-treasure-valley" className="block bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
              <h4 className="font-bold text-gray-900 mb-2">Bathroom Renovation ROI in Treasure Valley</h4>
              <p className="text-sm text-gray-600 mb-3">Learn how bathroom remodels compare to kitchen renovations for ROI in Boise and Meridian.</p>
              <span className="text-[#B45309] font-semibold text-sm flex items-center gap-2">
                Read More
                <Icon name="arrow-right" className="w-3.5 h-3.5" />
              </span>
            </a>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-[#1F2937] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="/images/logo.svg" alt="Fix Right Services" className="h-10 mb-4 brightness-0 invert" />
              <p className="text-gray-300 text-sm">Professional kitchen remodeling, handyman services, and home renovations in Boise, Idaho.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><a href="/#services" className="hover:text-white cursor-pointer">Kitchen Remodeling</a></li>
                <li><a href="/#services" className="hover:text-white cursor-pointer">Bathroom Renovation</a></li>
                <li><a href="/#services" className="hover:text-white cursor-pointer">Home Remodeling</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><a href="/blog" className="hover:text-white cursor-pointer">Blog</a></li>
                <li><a href="/#contact" className="hover:text-white cursor-pointer">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <p className="text-sm text-gray-300 mb-2">Boise, Idaho</p>
              <p className="text-sm text-gray-300">(208) 123-4567</p>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}